import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate as useReactNavigate } from "react-router-dom";
import { Plus, X, Calendar, Tag, Image as ImageIcon, FileText, Trash2, Edit, MessageCircle, Share2, Sparkles, Eye, ShoppingCart } from "lucide-react";
import { formatItalianDate } from "../utils/dateIt";
import { openAuthenticatedPdf, openAuthenticatedPdfPost } from "../utils/pdf";
import { shareWhatsAppWithPdf, openWhatsAppText, downloadBlob } from "../utils/share";
import { DatePickerIT } from "./DatePickerIT";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileUrl = (p) => (p?.startsWith("http") ? p : `${BACKEND_URL}/api/files/${p}`);

// Hook unread count
export const useOffersUnreadCount = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/offers/unread-count`);
        if (mounted) setCount(r.data?.total || 0);
      } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);
  return count;
};

export const OfferEditor = ({ offer, onClose, onSaved }) => {
  const [form, setForm] = useState(offer || {
    title: "", description: "", price: 0, unit: "Kg", images: [], notes: "",
    expires_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    product_id: null, product_code: "", category_id: null, category_name: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showCatalog, setShowCatalog] = useState(!!offer?.product_id);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const filteredProducts = !productSearch ? products.slice(0, 30)
    : products.filter(p =>
        (p.code || "").toLowerCase().includes(productSearch.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(productSearch.toLowerCase())
      ).slice(0, 30);

  const pickProduct = (p) => {
    setForm(f => ({
      ...f,
      title: f.title || p.description,
      product_id: p.id,
      product_code: p.code,
      category_id: p.category_id || null,
      category_name: p.category_name || "",
      unit: p.unit || "Kg",
      price: f.price || p.acq_price || 0,
    }));
    setProductSearch("");
    toast.success("Prodotto collegato all'offerta");
  };
  const clearProduct = () => setForm(f => ({ ...f, product_id: null, product_code: "", category_id: null, category_name: "" }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/offers/upload`, fd);
      setForm(f => ({ ...f, images: [...(f.images || []), r.data.path] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
    finally { setUploading(false); }
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.title || !form.price || !form.expires_at) { toast.error("Titolo, prezzo e scadenza obbligatori"); return; }
    setSaving(true);
    try {
      if (offer?.id) await axios.put(`${API}/offers/${offer.id}`, form);
      else await axios.post(`${API}/offers`, form);
      toast.success(offer?.id ? "Offerta aggiornata" : "Offerta pubblicata!");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[95vh] overflow-y-auto" data-testid="offer-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C5A059]" /> {offer?.id ? "Modifica offerta" : "Nuova offerta speciale"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Product picker: free OR catalog */}
          <div className="bg-stone-50 border border-stone-200 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold flex items-center gap-2"><Tag className="w-4 h-4 text-[#8A1C2A]" /> Articolo dell'offerta</Label>
              <div className="flex gap-1 text-xs">
                <button type="button" onClick={() => { setShowCatalog(false); clearProduct(); }} className={`px-2 py-1 rounded ${!showCatalog ? "bg-[#8A1C2A] text-white" : "bg-white border border-stone-300 text-stone-700"}`} data-testid="offer-mode-free">Libero</button>
                <button type="button" onClick={() => setShowCatalog(true)} className={`px-2 py-1 rounded ${showCatalog ? "bg-[#8A1C2A] text-white" : "bg-white border border-stone-300 text-stone-700"}`} data-testid="offer-mode-catalog">Dal catalogo</button>
              </div>
            </div>
            {!showCatalog ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Codice (opzionale)</Label><Input value={form.product_code || ""} onChange={e => setForm({ ...form, product_code: e.target.value, product_id: null })} placeholder="Es. OFF-001" className="bg-white border-stone-300" data-testid="offer-free-code" /></div>
                <div><Label className="text-xs">Categoria (opzionale)</Label><Input value={form.category_name || ""} onChange={e => setForm({ ...form, category_name: e.target.value, category_id: null })} placeholder="Es. Bovino" className="bg-white border-stone-300" data-testid="offer-free-category" /></div>
              </div>
            ) : form.product_id ? (
              <div className="flex items-center justify-between bg-white border border-stone-300 rounded p-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate"><span className="font-mono text-xs text-[#8A1C2A] mr-2">{form.product_code}</span>{form.category_name && <span className="text-[10px] bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded mr-1">{form.category_name}</span>}{form.title}</p>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={clearProduct} className="text-red-600"><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <div>
                <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Cerca per codice o descrizione..." className="bg-white border-stone-300" data-testid="offer-product-search" />
                <div className="max-h-44 overflow-y-auto border border-stone-200 rounded mt-2 bg-white">
                  {filteredProducts.length === 0 ? <p className="p-2 text-xs text-stone-500 text-center">Nessun prodotto</p>
                    : filteredProducts.map(p => (
                      <button key={p.id} type="button" onClick={() => pickProduct(p)} className="w-full text-left p-2 hover:bg-stone-50 border-b border-stone-100 text-sm flex items-center justify-between" data-testid={`offer-pick-${p.code}`}>
                        <div className="min-w-0">
                          <p className="truncate"><span className="font-mono text-[10px] text-[#8A1C2A] mr-1">{p.code}</span>{p.description}</p>
                          {p.category_name && <p className="text-[10px] text-stone-500 truncate">{p.category_name}</p>}
                        </div>
                        <Plus className="w-3 h-3 text-stone-400 shrink-0" />
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
          <div><Label>Titolo *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Es. Filetto di vitello irlandese" className="bg-white border-stone-300" data-testid="offer-title" /></div>
          <div><Label>Descrizione</Label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Frollatura 28gg, 1a scelta..." className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" data-testid="offer-description" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Prezzo € *</Label><Input type="number" step="0.01" min="0" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-white border-stone-300" data-testid="offer-price" /></div>
            <div><Label>U.M.</Label><Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="Kg" className="bg-white border-stone-300" /></div>
            <div className="col-span-2 sm:col-span-1"><Label>Scadenza *</Label><DatePickerIT value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} minDate={new Date()} data-testid="offer-expires" /></div>
          </div>
          <div><Label>Note speciali</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Es. Minimo 5kg, fino esaurimento" className="bg-white border-stone-300" /></div>
          <div>
            <Label>Foto del prodotto (max 6 — polaroid PDF)</Label>
            <div className="flex gap-2 flex-wrap mt-1">
              {(form.images || []).map((p, i) => (
                <div key={i} className="relative w-24 h-24 border-2 border-stone-200 rounded overflow-hidden">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-bl"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {(form.images?.length || 0) < 6 && (
                <label className="w-24 h-24 border-2 border-dashed border-stone-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 text-stone-500" data-testid="offer-image-upload">
                  <ImageIcon className="w-6 h-6" />
                  <span className="text-[10px] mt-1">{uploading ? "..." : "+ Foto"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="offer-save-btn">{saving ? "Salvataggio..." : (offer?.id ? "Aggiorna" : "Pubblica offerta")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Component used by Warehouse to manage offers
export const WarehouseOffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/offers?include_expired=true`); setOffers(r.data || []); }
    catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare l'offerta?")) return;
    try { await axios.delete(`${API}/offers/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const preview = (id) => openAuthenticatedPdf(`/offers/${id}/pdf`);

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#C5A059]" /> Offerte speciali</h1>
          <p className="text-stone-500 text-sm">Crea offerte con foto e prezzo, gli agenti potranno inoltrarle ai loro clienti</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="new-offer-btn"><Plus className="w-4 h-4 mr-1" /> Nuova offerta</Button>
      </div>
      {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : offers.length === 0 ? (
          <Card className="bg-white border-stone-200 p-8 text-center">
            <Sparkles className="w-12 h-12 mx-auto text-stone-300" />
            <p className="text-stone-500 mt-3">Nessuna offerta — crea la prima!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {offers.map(o => (
              <Card key={o.id} className={`bg-white p-3 ${o.is_expired ? "border-stone-300 opacity-60" : "border-[#C5A059]"}`} data-testid={`offer-card-${o.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-stone-900 flex-1 truncate">{o.title}</h3>
                  {o.is_expired && <Badge className="bg-stone-100 text-stone-600 text-[10px]">Scaduta</Badge>}
                </div>
                {o.images?.[0] && <img src={fileUrl(o.images[0])} alt="" className="w-full h-32 object-cover rounded mb-2 border border-stone-200" />}
                <p className="text-2xl font-bold text-[#8A1C2A]">€ {o.price.toFixed(2)} <span className="text-xs font-normal text-stone-500">/ {o.unit}</span></p>
                <p className="text-xs text-stone-600 mt-1 truncate">{o.description}</p>
                <p className="text-xs text-stone-500 mt-1">Valida fino al {formatItalianDate(o.expires_at)}</p>
                <div className="flex items-center gap-1 mt-2 -ml-1 flex-wrap">
                  <Button size="sm" variant="ghost" onClick={() => preview(o.id)} className="text-xs"><Eye className="w-3 h-3 mr-1" /> PDF</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(o)} className="text-xs"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(o.id)} className="text-xs text-red-600"><Trash2 className="w-3 h-3 mr-1" /> Elimina</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      {(creating || editing) && <OfferEditor offer={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />}
    </div>
  );
};

// Agent component to view + share offers
export const AgentOffersList = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // {offer_id: true}
  const [sharing, setSharing] = useState(false);
  const navigate = useReactNavigate();

  const load = async () => {
    setLoading(true);
    try { 
      const r = await axios.get(`${API}/offers`); 
      setOffers(r.data || []); 
      // Mark all as seen
      await axios.post(`${API}/offers/mark-seen`).catch(() => {});
    }
    catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const previewSingle = (id) => openAuthenticatedPdf(`/offers/${id}/pdf`);

  const previewVolantino = async () => {
    if (selectedIds.length === 0) { toast.error("Seleziona almeno un'offerta"); return; }
    openAuthenticatedPdfPost(`/offers/pdf-multi`, { offer_ids: selectedIds }, { filename: "Volantino-offerte.pdf" });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C5A059]" /> Offerte del magazzino</h2>
          <p className="text-xs text-stone-500">Seleziona quelle che vuoi inoltrare ai tuoi clienti, poi click su Inoltra</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedIds.length > 0 && <Button onClick={previewVolantino} variant="outline" className="border-[#C5A059] text-[#8A1C2A]" data-testid="preview-volantino-btn"><FileText className="w-4 h-4 mr-1" /> Volantino PDF ({selectedIds.length})</Button>}
          <Button onClick={() => setSharing(true)} disabled={selectedIds.length === 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="share-offers-btn"><Share2 className="w-4 h-4 mr-1" /> Inoltra ({selectedIds.length})</Button>
        </div>
      </div>
      {loading ? <div className="p-6 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : offers.length === 0 ? (
          <Card className="bg-white border-stone-200 p-8 text-center text-stone-500"><Sparkles className="w-10 h-10 mx-auto text-stone-300" /><p className="mt-2 text-sm">Nessuna offerta attiva</p></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {offers.map(o => (
              <Card key={o.id} className={`p-3 cursor-pointer transition-all ${selected[o.id] ? "bg-amber-50 border-[#C5A059] border-2" : "bg-white border-stone-200 hover:border-[#C5A059]"}`} onClick={() => toggle(o.id)} data-testid={`agent-offer-${o.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-stone-900 flex-1 truncate">{o.title}</h3>
                  <input type="checkbox" checked={!!selected[o.id]} readOnly className="w-4 h-4 accent-[#8A1C2A]" />
                </div>
                {o.images?.[0] && <img src={fileUrl(o.images[0])} alt="" className="w-full h-32 object-cover rounded mb-2 border border-stone-200" />}
                {o.category_name && <span className="inline-block text-[10px] text-[#8A1C2A] bg-[#8A1C2A]/10 px-2 py-0.5 rounded font-medium mb-1">{o.category_name}</span>}
                <p className="text-2xl font-bold text-[#8A1C2A]">€ {o.price.toFixed(2)} <span className="text-xs font-normal text-stone-500">/ {o.unit}</span></p>
                <p className="text-xs text-stone-600 mt-1 truncate">{o.description}</p>
                {o.notes && <p className="text-xs italic text-stone-500 mt-1">{o.notes}</p>}
                <p className="text-xs text-stone-500 mt-1">Valida fino al {formatItalianDate(o.expires_at)}</p>
                <div className="flex items-center gap-1 mt-1 -ml-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => previewSingle(o.id)} className="text-xs"><Eye className="w-3 h-3 mr-1" /> PDF</Button>
                  <Button size="sm" onClick={() => navigate(`/agent/new-order?offer_id=${o.id}`)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white text-xs h-7" data-testid={`agent-order-offer-${o.id}`}><ShoppingCart className="w-3 h-3 mr-1" /> Ordina</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      {sharing && <ShareOffersDialog offerIds={selectedIds} onClose={() => setSharing(false)} onDone={() => { setSharing(false); setSelected({}); }} />}
    </div>
  );
};

const ShareOffersDialog = ({ offerIds, onClose, onDone }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomers, setSelectedCustomers] = useState({});
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [shareMode, setShareMode] = useState("chat"); // 'chat' or 'whatsapp'

  useEffect(() => {
    axios.get(`${API}/customers`).then(r => setCustomers(r.data || [])).catch(() => toast.error("Errore caricamento clienti")).finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter(c => !search || c.business_name?.toLowerCase().includes(search.toLowerCase()) || c.codice_cliente?.toLowerCase().includes(search.toLowerCase()));
  const selectedIds = Object.keys(selectedCustomers).filter(k => selectedCustomers[k]);

  const sendViaChat = async () => {
    if (selectedIds.length === 0) { toast.error("Seleziona almeno un cliente"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/offers/share-via-chat`, { offer_ids: offerIds, customer_ids: selectedIds, message });
      toast.success(`Offerte inviate a ${r.data.sent_to} clienti`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSending(false); }
  };

  const sendViaWhatsapp = async () => {
    if (selectedIds.length === 0) { toast.error("Seleziona almeno un cliente"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/offers/pdf-multi`, { offer_ids: offerIds }, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (selectedIds.length === 1) {
        // Un solo cliente → share sheet nativo con PDF allegato (iPad/iPhone/Android)
        const c = customers.find((x) => x.id === selectedIds[0]);
        const text = `Ciao ${c?.contact_name || c?.business_name || ""}, ti inoltro le offerte speciali di Giotto Carni 🥩\n\n${message || ""}`.trim();
        const res = await shareWhatsAppWithPdf({ text, blob, filename: "Offerte.pdf", phone: c?.phone });
        if (res === "shared") toast.success("Offerte condivise con PDF allegato");
        onDone();
        return;
      }
      // Più clienti: scarica il PDF una volta + apri una chat WhatsApp per cliente
      downloadBlob(blob, "Offerte.pdf");
      for (const custId of selectedIds) {
        const c = customers.find((x) => x.id === custId);
        const text = `Ciao ${c?.contact_name || c?.business_name || ""}, ti inoltro le offerte speciali di Giotto Carni 🥩\n\n${message || ""}`.trim();
        openWhatsAppText(text, c?.phone);
      }
      toast.success("Chat WhatsApp aperte — allega il PDF scaricato a ciascun cliente");
      onDone();
    } catch { toast.error("Errore"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="share-offers-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#8A1C2A]" /> Inoltra {offerIds.length} offerta/e ai clienti</DialogTitle></DialogHeader>
        <div className="flex gap-2 border-b border-stone-200 -mt-2 -mx-6 px-6 pb-2">
          <button onClick={() => setShareMode("chat")} className={`px-3 py-1 rounded text-sm ${shareMode === "chat" ? "bg-[#8A1C2A] text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid="share-mode-chat">💬 Chat in-app</button>
          <button onClick={() => setShareMode("whatsapp")} className={`px-3 py-1 rounded text-sm ${shareMode === "whatsapp" ? "bg-green-600 text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid="share-mode-whatsapp">📱 WhatsApp</button>
        </div>
        <div>
          <Label>Messaggio di accompagnamento</Label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="Ti inoltro le offerte di oggi!" className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" data-testid="share-message" />
        </div>
        <Input placeholder="Cerca cliente..." value={search} onChange={e => setSearch(e.target.value)} className="bg-white border-stone-300" data-testid="share-search" />
        <div className="max-h-72 overflow-y-auto border border-stone-200 rounded">
          {loading ? <div className="p-6 text-center"><div className="spinner w-5 h-5 mx-auto" /></div>
            : filtered.length === 0 ? <p className="p-4 text-center text-sm text-stone-500">Nessun cliente</p>
              : filtered.map(c => (
                <label key={c.id} className="flex items-center gap-3 p-2 hover:bg-stone-50 cursor-pointer border-b border-stone-100">
                  <input type="checkbox" checked={!!selectedCustomers[c.id]} onChange={e => setSelectedCustomers(s => ({ ...s, [c.id]: e.target.checked }))} className="w-4 h-4 accent-[#8A1C2A]" data-testid={`customer-cb-${c.codice_cliente}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.business_name}</p>
                    <p className="text-xs text-stone-500 truncate">{c.codice_cliente} · {c.phone || "no telefono"} {!c.email && "· no email"}</p>
                  </div>
                  {shareMode === "chat" && !c.login_user_id && <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">No login</Badge>}
                </label>
              ))}
        </div>
        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          {shareMode === "chat" ? (
            <Button onClick={sendViaChat} disabled={sending || selectedIds.length === 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="send-chat-btn">
              <MessageCircle className="w-4 h-4 mr-1" /> {sending ? "..." : `Invia in chat a ${selectedIds.length}`}
            </Button>
          ) : (
            <Button onClick={sendViaWhatsapp} disabled={selectedIds.length === 0} className="bg-green-600 hover:bg-green-700 text-white" data-testid="send-whatsapp-btn">
              📱 Apri WhatsApp ({selectedIds.length})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OffersBadge = ({ onClick, "data-testid": testId = "offers-badge-btn" }) => {
  const unread = useOffersUnreadCount();
  return (
    <button onClick={onClick} className="relative p-2 rounded-md hover:bg-stone-100 text-stone-700" data-testid={testId} title="Offerte">
      <Sparkles className="w-5 h-5 text-[#C5A059]" />
      {unread > 0 && (
        <span className="absolute -top-1 -right-1 bg-[#C5A059] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread > 99 ? "99+" : unread}</span>
      )}
    </button>
  );
};
